import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import type { UpsellVariant } from "@/components/UpsellContent";

type GateOptions = {
  variant?: UpsellVariant;
  returnUrl?: string;
  /** Legacy no-op; kept for source compatibility. */
  openDesktopUpgrade?: () => void;
  /** Called when the user IS subscribed and can use the feature. */
  onAllowed: () => void;
};

/**
 * Centralised premium-gate decision used by every paid entry point.
 *
 * - Loading → no-op.
 * - Not signed in → /my-recipes (sign-in card).
 * - Expired trial → /upgrade/expired (personalised soft-lock page).
 * - Free or signed-out non-active → /upgrade with context.
 * - Active (paid or trialing) → run `onAllowed`.
 */
export const usePremiumGate = ({
  variant = "default",
  returnUrl,
  onAllowed,
}: GateOptions) => {
  const navigate = useNavigate();
  const { email, isActive, expiredTrial, loading } = useSubscription();

  const trigger = useCallback(() => {
    if (loading) return;
    if (!email) {
      navigate("/my-recipes");
      return;
    }
    if (isActive) {
      onAllowed();
      return;
    }
    const back = returnUrl || window.location.pathname + window.location.search;
    const params = new URLSearchParams();
    if (expiredTrial) {
      params.set("from", back);
      window.location.assign(`/upgrade/expired?${params.toString()}`);
      return;
    }
    if (variant !== "default") params.set("ctx", variant);
    params.set("from", back);
    window.location.assign(`/upgrade?${params.toString()}`);
  }, [loading, email, isActive, expiredTrial, navigate, variant, returnUrl, onAllowed]);

  return { trigger, loading, email, isActive, expiredTrial };
};
